import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { RadioButton, RadioGroup } from '@/lib/base/radio-buttons';
import { useWizardContext } from '@/lib/base/wizard';
import { logger } from '@/lib/logger';

import { selectMailchimpAudience } from '../../api/audiences';
import { getMailchimpFieldMappings } from '../../api/fieldMappings';
import { useMailchimpAudiences } from '../../hooks/useMailchimpAudiences';
import { ZeroAudiencesError } from '../ZeroAudiencesError';

interface WizardAudienceStepProps {
  orgId: number | string;
}

export function WizardAudienceStep({ orgId }: WizardAudienceStepProps) {
  const { next } = useWizardContext();
  const queryClient = useQueryClient();
  const audiences = useMailchimpAudiences(orgId, true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const audienceList = useMemo(
    () => audiences.data?.audiences ?? [],
    [audiences.data]
  );
  const isNoAudiences = audiences.data?.error_code === 'no_audiences';

  // Pre-select the first audience so the choice is one click, never auto-submit.
  useEffect(() => {
    if (!selectedId && audienceList.length > 0) {
      setSelectedId(audienceList[0].id);
    }
  }, [audienceList, selectedId]);

  const handleRadioChange = useCallback((value: string) => {
    setSelectedId(value);
  }, []);

  const handleContinue = useCallback(async () => {
    if (!selectedId) {
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await selectMailchimpAudience(orgId, selectedId);
      // select-audience runs on_connect, which creates the default field mappings and
      // caches the merge-field schema. Before advancing to the Fields step, load both
      // pieces of state into the query cache and AWAIT them, so the step renders with
      // data instead of an empty/stale snapshot:
      //   - status: refetched so `audience_id` (hasAudience) is set, which enables the
      //     gated mappings query in the wizard.
      //   - mappings: fetched imperatively (fetchQuery bypasses the gated hook's
      //     `enabled:false`, which would otherwise make invalidate a no-op) so the
      //     freshly-created mappings are in cache before the Fields step mounts.
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['mailchimpStatus', orgId] }),
        queryClient.fetchQuery({
          queryKey: ['mailchimpMappings', orgId],
          queryFn: () => getMailchimpFieldMappings(orgId),
          staleTime: 0,
        }),
      ]);
      next();
    } catch (err) {
      logger.error(
        'Error selecting Mailchimp audience',
        err instanceof Error ? err : new Error(String(err))
      );
      setError("We couldn't select that audience. Try again.");
      setIsSubmitting(false);
    }
  }, [orgId, selectedId, next, queryClient]);

  if (audiences.isLoading) {
    return (
      <div className='flex justify-center py-10'>
        <LoadingIndicator size='sm' />
      </div>
    );
  }

  if (isNoAudiences) {
    return <ZeroAudiencesError />;
  }

  return (
    <div className='space-y-6'>
      <div className='text-center'>
        <h2 className='text-heading-2 font-semibold text-[var(--color-text-primary)]'>
          Choose your audience
        </h2>
        <p className='text-body mt-1 text-[var(--color-text-tertiary)]'>
          Pick the Mailchimp audience to keep in sync with Ahoy contacts.
        </p>
      </div>

      <RadioGroup
        value={selectedId ?? ''}
        onChange={handleRadioChange}
        className='gap-2'
      >
        {audienceList.map(audience => (
          <RadioButton
            key={audience.id}
            value={audience.id}
            label={audience.name}
            hint={`${audience.member_count.toLocaleString()} members`}
          />
        ))}
      </RadioGroup>

      {error && (
        <p className='text-sm text-[var(--color-text-error-primary)]'>
          {error}
        </p>
      )}

      <div className='flex justify-end'>
        <Button
          color='primary'
          size='md'
          onClick={handleContinue}
          isDisabled={!selectedId || isSubmitting}
          isLoading={isSubmitting}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
